import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Target, TrendingUp, Trophy, Star, CreditCard, CalendarPlus, MessageSquare, MessageCircle, Phone, ClipboardList, CheckCircle2, Circle, Loader } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../lib/AuthContext";
import { PT_QUICK_TAGS, ptErrorMessage } from "../lib/ptI18n";
import PTBook from "../components/pt/PTBook";
import PTIntake from "../components/pt/PTIntake";
import PTLanguageToggle from "../components/pt/PTLanguageToggle";

const STATUS_COLORS = { pending: "#D4A93A", approved: "#4ADE80", declined: "#F87171", cancelled: "#F87171", completed: "#4ADE80", no_show: "#F87171" };
const STATUS_KEYS = { pending: "pending", approved: "approved", completed: "completed", cancelled: "cancelled", declined: "declined", no_show: "noShow" };
const TABS = ["overview", "todo", "book", "bookings", "traject", "package", "profile"];

function calLinks(b, address, t) {
  const dt = (d, time) => `${d.replace(/-/g, "")}T${time.replace(":", "")}00`;
  const endT = `${String(parseInt(b.time) + 1).padStart(2, "0")}:00`;
  const title = encodeURIComponent("Personal Training — JOH Boxing");
  const loc = encodeURIComponent(address || "Rhoneweg 38, 1043 AH Amsterdam");
  const details = encodeURIComponent(t("pt.calendar.description"));
  return {
    google: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dt(b.date, b.time)}/${dt(b.date, endT)}&ctz=Europe/Amsterdam&details=${details}&location=${loc}`,
    outlook: `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${b.date}T${b.time}:00&enddt=${b.date}T${endT}:00&body=${details}&location=${loc}`,
  };
}

function downloadIcs(b, address, t) {
  const dt = (d, time) => `${d.replace(/-/g, "")}T${time.replace(":", "")}00`;
  const endT = `${String(parseInt(b.time) + 1).padStart(2, "0")}:00`;
  const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//JOH Boxing//PT//NL", "BEGIN:VEVENT",
    `UID:pt-${b.id}@johboxing.com`, `DTSTART;TZID=Europe/Amsterdam:${dt(b.date, b.time)}`, `DTEND;TZID=Europe/Amsterdam:${dt(b.date, endT)}`,
    "SUMMARY:Personal Training — JOH Boxing", `LOCATION:${(address || "Rhoneweg 38, 1043 AH Amsterdam").replace(/,/g, "\\,")}`,
    `DESCRIPTION:${t("pt.calendar.description")}`, "END:VEVENT", "END:VCALENDAR"].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url; a.download = `PT-${b.date}-${b.time.replace(":", "")}.ics`; a.click();
  URL.revokeObjectURL(url);
}

export default function PTAccount() {
  const { t, lang } = useT();
  const nav = useNavigate();
  const { user, ready, signOut } = useAuth();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");
  const [intakeOpen, setIntakeOpen] = useState(false);

  const load = () => { if (user) api.get("/pt/me").then(({ data }) => setMe(data)).catch(() => setMe({ bookings: [], todos: [] })); };
  useEffect(() => { if (ready && !user) nav("/pt"); }, [ready, user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(load, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ready || !user) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><span className="chip text-[#A3A3A3]" data-testid="pt-dashboard-loading">{t("pt.common.loading")}</span></div>;
  }
  const today = new Date().toISOString().slice(0, 10);
  const items = me?.bookings || [];
  const upcoming = items.filter((b) => b.date >= today && ["pending", "approved"].includes(b.status)).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
  const past = items.filter((b) => ["completed", "no_show"].includes(b.status) || (b.date < today && b.status === "approved"));
  const next = me?.next_session || upcoming[0];
  const pkg = me?.active_package;
  const profile = me?.profile || {};
  const openPay = (me?.open_payments || [])[0];
  const todos = me?.todos || [];
  const openTodos = todos.filter((todo) => todo.state !== "completed");
  const done = me?.sessions_done || 0;
  const total = me?.sessions_total || (pkg ? pkg.total : 0);

  const openTodo = (todo) => {
    if (todo.action === "intake") { setTab("todo"); setIntakeOpen(true); }
    else if (todo.action === "payment") { setTab("package"); }
    else { setTab("todo"); }
  };

  const statusLabel = (status) => t(`pt.common.status.${STATUS_KEYS[status] || status}`).toUpperCase();
  const TodoIcon = ({ state }) => state === "completed" ? <CheckCircle2 size={15} color="#4ADE80"/> : state === "in_progress" ? <Loader size={15} color="#D4A93A"/> : <Circle size={15} color="#D4A93A"/>;
  const stateLabel = (state) => t(state === "completed" ? "pt.common.state.completed" : state === "in_progress" ? "pt.common.state.inProgress" : "pt.common.state.todo").toUpperCase();

  const todoRow = (todo) => (
    <div key={todo.key} className="line-b line-dark py-4 flex flex-wrap items-center gap-3" data-testid={`pt-todo-${todo.key}`}>
      <TodoIcon state={todo.state}/>
      <div className="flex-1 min-w-48">
        <div className="font-display uppercase text-lg">{todo.title}</div>
        {todo.state !== "completed" && todo.subtitle && <div className="text-sm text-[#A3A3A3] mt-0.5">{todo.subtitle}</div>}
        {todo.state === "completed" && todo.key === "intake" && (
          <div className="chip text-[#4A4A4A] mt-1">{t("pt.dash.intakeCompleted")} — {t("pt.dash.intakeReceived")}{todo.completed_at ? ` · ${todo.completed_at.slice(0, 10)}` : ""}</div>
        )}
      </div>
      <span className="chip px-2 py-0.5 border" style={todo.state === "completed" ? { color: "#4ADE80", borderColor: "#4ADE8055" } : { color: "#D4A93A", borderColor: "#D4A93A55" }}>
        {stateLabel(todo.state)}
      </span>
      {todo.state !== "completed" && todo.key === "intake" && (
        <button type="button" onClick={() => openTodo(todo)} className="chip px-4 py-2 border transition-colors" style={{ borderColor: "var(--joh-accent)", color: "var(--joh-accent)" }} data-testid="pt-todo-start-intake">{t("pt.dash.startIntake").toUpperCase()}</button>
      )}
      {todo.state !== "completed" && todo.action === "payment" && (
        <button type="button" onClick={() => openTodo(todo)} className="chip px-4 py-2 border transition-colors" style={{ borderColor: "var(--joh-accent)", color: "var(--joh-accent)" }} data-testid="pt-todo-pay">{t("pt.common.payNow").toUpperCase()}</button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F3F0] grain grain-dark" data-testid="pt-dashboard">
      <header className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-[#F5F3F0]/10">
        <Link to="/pt" className="font-logo text-2xl tracking-tight" data-testid="pt-dash-logo">JOH.</Link>
        <div className="flex items-center gap-5">
          <PTLanguageToggle compact />
          <Link to="/pt" className="chip text-[#DDD6CB] hover:text-white" data-testid="pt-dash-topage">← {t("pt.common.ptPage").toUpperCase()}</Link>
          <button type="button" onClick={async () => { await signOut(); nav("/pt"); }} className="chip text-[#A3A3A3] hover:text-[#F5F3F0] inline-flex items-center gap-1.5" data-testid="pt-dash-logout"><LogOut size={12}/> {t("pt.common.logout").toUpperCase()}</button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 md:py-12">
        <h1 className="font-display uppercase text-3xl md:text-5xl mb-6" data-testid="pt-dash-welcome">
          {t("pt.dash.welcome")}, {user.first_name || user.name}.
        </h1>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-8 -mx-1 px-1" data-testid="pt-dash-tabs">
          {TABS.map((key) => (
            <button type="button" key={key} onClick={() => setTab(key)}
              className={`chip px-3.5 py-2 border whitespace-nowrap transition-colors ${tab === key ? "text-[#0A0A0A]" : "border-[#F5F3F0]/20 text-[#A3A3A3]"}`}
              style={tab === key ? { background: "var(--joh-accent)", borderColor: "var(--joh-accent)" } : {}}
              data-testid={`pt-dash-tab-${key}`}>
              {t(`pt.dash.tab.${key}`).toUpperCase()}{key === "todo" && openTodos.length > 0 ? ` (${openTodos.length})` : ""}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div data-testid="pt-dash-overview">
            {openTodos.length > 0 && (
              <div className="mb-8">
                <div className="chip mb-3" style={{ color: "var(--joh-accent)" }}><ClipboardList size={12} className="inline -mt-0.5"/> — {t("pt.dash.tab.todo").toUpperCase()}</div>
                <div className="line-t line-dark">{openTodos.map(todoRow)}</div>
              </div>
            )}
            <div className="border p-6 mb-4" style={{ borderColor: next ? "var(--joh-accent)" : "#F5F3F022" }} data-testid="pt-dash-next">
              <div className="chip text-[#A3A3A3]">{t("pt.dash.nextSession").toUpperCase()}</div>
              {next ? (
                <>
                  <div className="font-display text-3xl md:text-4xl mt-3">{next.date} · {next.time}–{String(parseInt(next.time) + 1).padStart(2, "0")}:00</div>
                  <div className="chip mt-2" style={{ color: STATUS_COLORS[next.status] }}>{statusLabel(next.status)}</div>
                  <div className="chip text-[#4A4A4A] mt-2">{me?.location} — {me?.location_address}</div>
                </>
              ) : (
                <div className="chip text-[#4A4A4A] mt-3">{t("pt.dash.noSession")}</div>
              )}
            </div>
            <div className="grid md:grid-cols-3 gap-3 mb-6">
              <div className="border border-[#F5F3F0]/15 p-5" data-testid="pt-dash-package-mini">
                <div className="chip text-[#A3A3A3]">{t("pt.dash.tab.package").toUpperCase()}</div>
                <div className="font-display text-3xl mt-2" style={{ color: pkg ? "var(--joh-accent)" : "#4A4A4A" }}>{pkg ? `${pkg.remaining} / ${pkg.total}` : "—"}</div>
                <div className="chip text-[#4A4A4A] mt-1">{pkg ? t("pt.dash.sessionsRemaining") : t("pt.dash.noActivePackage")}</div>
              </div>
              <div className="border border-[#F5F3F0]/15 p-5" data-testid="pt-dash-requests">
                <div className="chip text-[#A3A3A3]">{t("pt.dash.currentRequests").toUpperCase()}</div>
                <div className="font-display text-3xl mt-2" style={{ color: (me?.pending_requests || 0) > 0 ? "#D4A93A" : "#DDD6CB" }}>{me?.pending_requests || 0}</div>
                <div className="chip text-[#4A4A4A] mt-1">{t("pt.dash.awaitingConfirmation")}</div>
              </div>
              <button type="button" onClick={() => setTab("book")} className="border p-5 text-left transition-colors hover:border-[#F5F3F0]/50" style={{ borderColor: "var(--joh-accent)" }} data-testid="pt-dash-quick-book">
                <div className="chip" style={{ color: "var(--joh-accent)" }}>{t("pt.dash.quickAction").toUpperCase()}</div>
                <div className="font-display text-2xl mt-2 inline-flex items-center gap-2"><CalendarPlus size={18}/> {t("pt.dash.bookSession").toUpperCase()}</div>
              </button>
            </div>
            <div className="border border-[#F5F3F0]/15 p-5" data-testid="pt-contact-coach">
              <a href="tel:+31626923741" className="btn-pt-cta justify-center" data-testid="pt-contact-coach-button">
                {t("pt.dash.contactCoach").toUpperCase()}
              </a>
              <div className="mt-4 flex flex-wrap items-center gap-3 chip text-[#DDD6CB]">
                <Phone size={14} style={{ color: "var(--joh-accent)" }} />
                <a href="tel:+31626923741" className="underline underline-offset-4" data-testid="pt-coach-phone">+31 6 26923741</a>
                <a href="https://wa.me/31626923741" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#25D366] hover:text-[#4ADE80] transition-colors" data-testid="pt-coach-whatsapp">
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}

        {tab === "todo" && (
          <div data-testid="pt-dash-todo">
            <div className="chip text-[#A3A3A3] mb-4">— {t("pt.dash.tab.todo").toUpperCase()}</div>
            {openTodos.length === 0 && !intakeOpen ? (
              <div className="border border-[#F5F3F0]/15 p-8 text-center" data-testid="pt-todo-caught-up">
                <CheckCircle2 size={22} className="mx-auto" color="#4ADE80"/>
                <div className="font-display uppercase text-2xl mt-3">{t("pt.dash.caughtUp")}</div>
              </div>
            ) : <div className="line-t line-dark mb-6">{todos.map(todoRow)}</div>}
            {intakeOpen && me?.intake_status === "intake_required" && (
              <PTIntake draft={me?.intake_draft} onDone={() => { setIntakeOpen(false); load(); }} />
            )}
            {me?.intake_status !== "intake_required" && !intakeOpen && (
              <div className="border border-[#F5F3F0]/15 p-5" data-testid="pt-intake-done-card">
                <div className="chip" style={{ color: "#4ADE80" }}>✓ {t("pt.dash.intakeCompleted").toUpperCase()}</div>
                <div className="text-sm text-[#A3A3A3] mt-2">{t("pt.dash.intakeReceived")}{me?.intake_completed_at ? ` ${t("pt.dash.submittedOn")} ${me.intake_completed_at.slice(0, 10)}.` : ""}</div>
              </div>
            )}
          </div>
        )}

        {tab === "book" && <PTBook pkg={pkg} onDone={load} />}

        {tab === "bookings" && (
          <div data-testid="pt-dash-bookings">
            <div className="chip text-[#A3A3A3] mb-3">— {t("pt.dash.upcoming").toUpperCase()}</div>
            {upcoming.length === 0 ? <div className="chip text-[#4A4A4A] py-3">{t("pt.dash.noUpcoming")}</div> : (
              <div className="line-t line-dark">{upcoming.map((booking) => <Row key={booking.id} b={booking} address={me?.location_address} cancelHours={me?.cancel_hours} onCancelled={load} />)}</div>
            )}
            <div className="chip text-[#A3A3A3] mt-8 mb-3">— {t("pt.dash.past").toUpperCase()}</div>
            {past.length === 0 ? <div className="chip text-[#4A4A4A] py-3">{t("pt.dash.noPast")}</div> : (
              <div className="line-t line-dark">{past.map((booking) => <Row key={booking.id} b={booking} />)}</div>
            )}
          </div>
        )}

        {tab === "traject" && (
          <div data-testid="pt-dash-traject">
            <div className="border border-[#F5F3F0]/15 p-6 mb-10" data-testid="pt-dash-progress">
              <div className="chip text-[#A3A3A3] mb-4">— {t("pt.dash.myProgress").toUpperCase()}</div>
              <div className="flex items-end gap-4 flex-wrap">
                <div className="flex-1 min-w-48">
                  <div className="chip text-[#DDD6CB] mb-2">{total ? `${done} / ${total} ${t("pt.dash.sessionsCompleted").toUpperCase()}` : `${done} ${t("pt.dash.sessionsDone").toUpperCase()}`}</div>
                  {total > 0 && (
                    <div className="h-2 bg-[#F5F3F0]/10 w-full" data-testid="pt-dash-progressbar">
                      <div className="h-full" style={{ width: `${Math.min(100, Math.round((done / total) * 100))}%`, background: "var(--joh-accent)" }} />
                    </div>
                  )}
                </div>
                {me?.last_session && <div className="chip text-[#4A4A4A]">{t("pt.dash.lastSession").toUpperCase()}: {me.last_session.date}</div>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
                <div className="border border-[#F5F3F0]/10 p-4">
                  <Target size={14} style={{ color: "var(--joh-accent)" }}/>
                  <div className="chip text-[#A3A3A3] mt-2">{t("pt.dash.goal").toUpperCase()}</div>
                  <div className="text-sm mt-1" data-testid="pt-dash-goal">{profile.goal || "—"}{profile.package_preference ? <span className="chip text-[#4A4A4A] block mt-1">{t("pt.dash.preference").toUpperCase()}: {profile.package_preference.toUpperCase()}</span> : null}</div>
                </div>
                <div className="border border-[#F5F3F0]/10 p-4">
                  <TrendingUp size={14} style={{ color: "var(--joh-accent)" }}/>
                  <div className="chip text-[#A3A3A3] mt-2">{t("pt.dash.currentFocus").toUpperCase()}</div>
                  <div className="text-sm mt-1" data-testid="pt-dash-focus">{profile.current_focus || "—"}</div>
                </div>
                <div className="border border-[#F5F3F0]/10 p-4">
                  <Trophy size={14} style={{ color: "var(--joh-accent)" }}/>
                  <div className="chip text-[#A3A3A3] mt-2">{t("pt.dash.milestones").toUpperCase()}</div>
                  <div className="text-sm mt-1" data-testid="pt-dash-milestone">{(profile.milestones || []).slice(-1)[0]?.text || "—"}</div>
                </div>
              </div>
            </div>
            <div className="chip text-[#A3A3A3] mb-4">— {t("pt.dash.trainingFeedback").toUpperCase()}</div>
            {past.filter((b) => b.status === "completed").length === 0 ? (
              <div className="border border-[#F5F3F0]/15 p-6 chip text-[#4A4A4A]">{t("pt.dash.noFeedback")}</div>
            ) : (
              <div className="space-y-3">
                {past.filter((b) => b.status === "completed").map((booking) => <FeedbackCard key={booking.id} b={booking} onDone={load} />)}
              </div>
            )}
          </div>
        )}

        {tab === "package" && (
          <div data-testid="pt-dash-package">
            {openPay && (
              <div className="border p-6 mb-6 flex flex-wrap items-center gap-4" style={{ borderColor: "#D4A93A" }} data-testid="pt-dash-payment">
                <div className="flex-1 min-w-48">
                  <div className="chip" style={{ color: "#D4A93A" }}>{t("pt.dash.outstandingPayment").toUpperCase()}</div>
                  <div className="font-display text-2xl mt-2 uppercase">{openPay.label}</div>
                  <div className="chip text-[#A3A3A3] mt-1">€{(openPay.amount / 100).toFixed(0)}</div>
                </div>
                <a href={openPay.checkout_url} className="btn-pt-cta justify-center" data-testid="pt-dash-pay"><CreditCard size={14}/> {t("pt.common.payNow").toUpperCase()}</a>
              </div>
            )}
            <div className="border border-[#F5F3F0]/15 p-6" data-testid="pt-dash-package-detail">
              {pkg ? (
                <>
                  <div className="font-display text-4xl" style={{ color: "var(--joh-accent)" }} data-testid="pt-dash-remaining">{pkg.remaining} / {pkg.total}</div>
                  <div className="chip text-[#A3A3A3] mt-2">{t("pt.dash.sessionsRemaining").toUpperCase()} · {t("pt.dash.validUntil")} {pkg.valid_until?.slice(0, 10)}</div>
                  <div className="chip text-[#4A4A4A] mt-1">{pkg.label}</div>
                  {(() => { const days = Math.ceil((new Date(pkg.valid_until) - Date.now()) / 86400000); return days <= 7 && days >= 0 && pkg.remaining > 0 ? (
                    <div className="chip mt-3 px-3 py-2 border inline-block" style={{ color: "#D4A93A", borderColor: "#D4A93A55" }} data-testid="pt-dash-expiry-warning">
                      {t("pt.dash.expiryWarning", { days, plural: days === 1 ? "" : (lang === "en" ? "s" : "en") })}
                    </div>
                  ) : null; })()}
                </>
              ) : (
                <>
                  <div className="chip text-[#4A4A4A]">{t("pt.dash.noActivePackage")}</div>
                  <Link to="/pt#pt-tarieven" className="chip mt-3 inline-block underline underline-offset-4" style={{ color: "var(--joh-accent)" }} data-testid="pt-dash-buy">{t("pt.dash.viewPackages")}</Link>
                </>
              )}
            </div>
            {(me?.paid_payments || []).length > 0 && (
              <>
                <div className="chip text-[#4A4A4A] mt-8 mb-3">— {t("pt.dash.payments").toUpperCase()}</div>
                <div className="line-t line-dark" data-testid="pt-dash-paid">
                  {me.paid_payments.map((payment) => (
                    <div key={payment.id} className="line-b line-dark py-3 flex items-center gap-4">
                      <span className="text-sm flex-1">{payment.label}</span>
                      <span className="chip text-[#A3A3A3]">€{(payment.amount / 100).toFixed(0)}</span>
                      <span className="chip px-2 py-0.5 border" style={{ color: "#4ADE80", borderColor: "#4ADE8055" }}>{t("pt.common.paid").toUpperCase()}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "profile" && (
          <div className="grid md:grid-cols-2 gap-3" data-testid="pt-dash-profile">
            {[
              [t("pt.dash.name"), user.name || `${user.first_name} ${user.last_name}`],
              [t("pt.common.email"), user.email],
              [t("pt.common.phone"), user.phone || "—"],
              [t("pt.common.gender"), user.gender === "male" ? t("pt.common.male") : user.gender === "female" ? t("pt.common.female") : "—"],
              [t("pt.common.dob"), user.dob || "—"],
              [t("pt.common.address"), user.address ? `${user.address.street} ${user.address.house_number}${user.address.addition ? ` ${user.address.addition}` : ""}, ${user.address.postal_code} ${user.address.city}` : "—"],
              [t("pt.auth.packagePreference"), profile.package_preference ? profile.package_preference.toUpperCase() : "—"],
            ].map(([label, value], i) => (
              <div key={i} className="border border-[#F5F3F0]/15 p-5">
                <div className="chip text-[#A3A3A3]">{label}</div>
                <div className="text-sm text-[#DDD6CB] mt-2">{value}</div>
              </div>
            ))}
            <div className="border border-[#F5F3F0]/15 p-5">
              <div className="chip text-[#A3A3A3]">{t("pt.dash.contactJoh")}</div>
              <div className="text-sm mt-2 space-x-4">
                <a href="tel:+31626923741" className="underline underline-offset-4" style={{ color: "var(--joh-accent)" }}>+31 6 26923741</a>
                <a href="mailto:team@johboxing.com" className="underline underline-offset-4" style={{ color: "var(--joh-accent)" }}>team@johboxing.com</a>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Row({ b, address, cancelHours, onCancelled }) {
  const { t } = useT();
  const [cancelBusy, setCancelBusy] = useState(false);
  const links = b.status === "approved" && b.date >= new Date().toISOString().slice(0, 10) ? calLinks(b, address, t) : null;
  const cancellable = cancelHours != null && ["pending", "approved"].includes(b.status)
    && new Date(`${b.date}T${b.time}:00`) >= new Date(Date.now() + cancelHours * 3600 * 1000);
  const cancel = async () => {
    if (!window.confirm(t("pt.dash.cancelConfirm"))) return;
    setCancelBusy(true);
    try {
      await api.post(`/pt/me/bookings/${b.id}/cancel`);
      toast.success(t("pt.dash.cancelledToast"));
      onCancelled?.();
    } catch (err) {
      toast.error(ptErrorMessage(err?.response?.data?.detail, t) || t("pt.dash.cancelFailed"));
    }
    setCancelBusy(false);
  };
  return (
    <div className="line-b line-dark py-3" data-testid={`pt-dash-booking-${b.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="chip text-[#DDD6CB] w-36">{b.date} · {b.time}</span>
        <span className="text-sm flex-1">PT · {b.booking_type === "package" ? t("pt.common.package") : t("pt.common.single")}</span>
        <span className="chip px-2 py-0.5 border" style={{ color: STATUS_COLORS[b.status], borderColor: "currentColor" }}>{t(`pt.common.status.${STATUS_KEYS[b.status] || b.status}`).toUpperCase()}</span>
        {cancellable && (
          <button type="button" onClick={cancel} disabled={cancelBusy}
            className="chip text-red-400/80 hover:text-red-300 transition-colors disabled:opacity-40"
            title={t("pt.dash.selfCancelTitle", { hours: cancelHours })}
            data-testid={`pt-cancel-${b.id}`}>
            {cancelBusy ? "…" : t("pt.common.cancel")}
          </button>
        )}
      </div>
      {links && (
        <div className="flex flex-wrap gap-3 mt-2" data-testid={`pt-cal-${b.id}`}>
          <span className="chip text-[#4A4A4A]">{t("pt.common.addToCalendar").toUpperCase()}:</span>
          <a href={links.google} target="_blank" rel="noreferrer" className="chip underline underline-offset-4" style={{ color: "var(--joh-accent)" }} data-testid={`pt-cal-google-${b.id}`}>Google</a>
          <button type="button" onClick={() => downloadIcs(b, address, t)} className="chip underline underline-offset-4 text-[#A3A3A3] hover:text-white" data-testid={`pt-cal-ics-${b.id}`}>Apple / .ics</button>
          <a href={links.outlook} target="_blank" rel="noreferrer" className="chip underline underline-offset-4 text-[#A3A3A3] hover:text-white" data-testid={`pt-cal-outlook-${b.id}`}>Outlook</a>
        </div>
      )}
    </div>
  );
}

function FeedbackCard({ b, onDone }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [tags, setTags] = useState([]);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const fb = b.feedback;
  const cf = b.client_feedback;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/pt/feedback/${b.id}`, { rating: rating || null, tags, comment });
      toast.success(t("pt.dash.feedbackThanks"));
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(ptErrorMessage(err?.response?.data?.detail, t) || t("pt.dash.feedbackFailed"));
    }
    setBusy(false);
  };

  return (
    <div className="border border-[#F5F3F0]/15 p-5" data-testid={`pt-fb-${b.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="chip text-[#DDD6CB]">{b.date} · {b.time}</span>
        <span className="chip px-2 py-0.5 border" style={{ color: "#4ADE80", borderColor: "#4ADE8055" }}>{t("pt.common.status.completed").toUpperCase()}</span>
        {cf && <span className="chip" style={{ color: "var(--joh-accent)" }}>{"★".repeat(cf.rating || 0)}{cf.rating ? "" : "✓"} {t("pt.dash.yourFeedbackSent").toUpperCase()}</span>}
      </div>
      {fb ? (
        <div className="mt-4 space-y-2" data-testid={`pt-fb-trainer-${b.id}`}>
          {fb.went_well && <div><span className="chip text-[#A3A3A3]">{t("pt.dash.whatWentWell").toUpperCase()}</span><p className="text-sm text-[#DDD6CB] mt-1">{fb.went_well}</p></div>}
          {fb.focus && <div><span className="chip text-[#A3A3A3]">{t("pt.dash.focusPoint").toUpperCase()}</span><p className="text-sm text-[#DDD6CB] mt-1">{fb.focus}</p></div>}
          {fb.next_step && <div><span className="chip text-[#A3A3A3]">{t("pt.dash.nextStep").toUpperCase()}</span><p className="text-sm text-[#DDD6CB] mt-1">{fb.next_step}</p></div>}
        </div>
      ) : (
        <p className="chip text-[#4A4A4A] mt-3">{t("pt.dash.trainerFeedbackFollows")}</p>
      )}
      {!cf && (
        <div className="mt-4">
          {!open ? (
            <button type="button" onClick={() => setOpen(true)} className="chip px-3 py-2 border border-[#F5F3F0]/25 text-[#DDD6CB] hover:text-white inline-flex items-center gap-2 transition-colors" data-testid={`pt-fb-open-${b.id}`}>
              <MessageSquare size={12}/> {t("pt.dash.giveFeedback")}
            </button>
          ) : (
            <div className="border border-[#F5F3F0]/15 p-4 mt-2" data-testid={`pt-fb-form-${b.id}`}>
              <div className="chip text-[#A3A3A3] mb-2">{t("pt.dash.howWasSession")}</div>
              <div className="flex gap-1.5 mb-3">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setRating(n)} data-testid={`pt-fb-star-${b.id}-${n}`}>
                    <Star size={22} fill={rating >= n ? "var(--joh-accent)" : "none"} color={rating >= n ? "var(--joh-accent)" : "#4A4A4A"} />
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {PT_QUICK_TAGS.map(([value, labelKey]) => (
                  <button key={value} type="button" onClick={() => setTags((p) => p.includes(value) ? p.filter((x) => x !== value) : [...p, value])}
                    className="chip px-2.5 py-1 border transition-colors"
                    style={tags.includes(value) ? { background: "var(--joh-accent)", color: "#0A0A0A", borderColor: "var(--joh-accent)" } : { borderColor: "#F5F3F033", color: "#A3A3A3" }}
                    data-testid={`pt-fb-tag-${b.id}-${value}`}>{t(labelKey)}</button>
                ))}
              </div>
              <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("pt.dash.anythingToAdd")} className="editorial-input dark w-full mb-3" data-testid={`pt-fb-comment-${b.id}`} />
              <div className="flex gap-3">
                <button type="button" onClick={submit} disabled={busy || (!rating && !tags.length && !comment)} className="chip px-4 py-2.5 disabled:opacity-40" style={{ background: "var(--joh-accent)", color: "#0A0A0A" }} data-testid={`pt-fb-submit-${b.id}`}>{busy ? "…" : t("pt.dash.sendFeedback").toUpperCase()}</button>
                <button type="button" onClick={() => setOpen(false)} className="chip text-[#4A4A4A]">{t("pt.common.cancel")}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
